import { useMutation } from "@apollo/client";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import DrawIcon from "@mui/icons-material/Draw";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import ArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import PersonIcon from "@mui/icons-material/Person";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import SendIcon from "@mui/icons-material/Send";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import TuneIcon from "@mui/icons-material/Tune";
import {
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Tooltip,
  Typography,
  DialogContentText,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import {
  BulkAddReviewersDialog,
  BulkSetSignerDialog,
  BulkActionSubmission,
} from "components/BulkActions";
import { ConfirmDialog } from "components/ConfirmDialog";
import { ChangeEncampStatusDialog } from "components/Submission/ChangeEncampStatusDialog";
import { VALID_ENCAMP_STATUSES } from "encamp-shared/src/submissions";
import { SubmissionMaterializationType, SubmissionStatus } from "generated-graphql/graphql";
import { EXCLUDE_SERIES_OCCURRENCES_MUTATION } from "queries/submissions";
import { useState, useCallback, useMemo } from "react";
import { BulkCreateRevisionSubmissionsDialog } from "routes/Customer/Waste/Submissions/BulkCreateRevisionSubmissionsDialog";

import {
  SET_SUBMISSION_ASSIGNEES_STAFF_MUTATION,
  CLEAR_SUBMISSION_ASSIGNEES_MUTATION,
} from "./api";
import { AssignDialog } from "./AssignDialog";
import { ChangeDueDateDialog } from "./ChangeDueDateDialog";
import { ChangeStatusDialog } from "./ChangeStatusDialog";
import { Row } from "./columns";
import { DeleteSubmissionsDialog } from "./DeleteSubmissionsDialog";
import { SendToReviewersDialog } from "./SendToReviewersDialog";

/**
 * Transforms Waste Fulfillment grid Row to common BulkActionSubmission format.
 */
function toBulkActionSubmission(row: Row): BulkActionSubmission {
  return {
    id: row.id,
    submissionId: row.submissionId ?? null,
    isVirtualEvent: row.materializationType === SubmissionMaterializationType.VirtualEvent,
    status: row.submissionStatus ?? null,
    dueDate: row.deadline ?? null,
    facilityName: row.facilities?.map((f) => f.name).join(", ") || row.facilityName || "",
    submissionType: row.submissionType ?? null,
    title: row.title ?? null,
    reviewerCount: 0,
    tenantId: row.tenantId ?? null,
  };
}

interface BulkActionsMenuProps {
  selectedRows: Row[];
  selectedRowIds: string[];
  /** IDs of rows that have materialized submissions */
  selectedSubmissionIds: string[];
  /** IDs of virtual events (not yet materialized) */
  selectedVirtualEventIds: string[];
  onSuccess: () => void;
  onClearSelection: () => void;
}

export function BulkActionsMenu({
  selectedRows,
  selectedRowIds,
  selectedSubmissionIds,
  selectedVirtualEventIds,
  onSuccess,
  onClearSelection,
}: BulkActionsMenuProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [assignOwnerDialogOpen, setAssignOwnerDialogOpen] = useState(false);
  const [changeStatusDialogOpen, setChangeStatusDialogOpen] = useState(false);
  const [changeDueDateDialogOpen, setChangeDueDateDialogOpen] = useState(false);
  const [sendToReviewersDialogOpen, setSendToReviewersDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [excludeDialogOpen, setExcludeDialogOpen] = useState(false);
  const [bulkAddReviewersDialogOpen, setBulkAddReviewersDialogOpen] = useState(false);
  const [bulkSetSignerDialogOpen, setBulkSetSignerDialogOpen] = useState(false);
  const [changeEncampStatusDialogOpen, setChangeEncampStatusDialogOpen] = useState(false);
  const [createRevisionsDialogOpen, setCreateRevisionsDialogOpen] = useState(false);

  const { success, error } = useAlerts();

  const open = Boolean(anchorEl);
  const hasVirtualEvents = selectedVirtualEventIds.length > 0;
  const hasMaterializedSubmissions = selectedSubmissionIds.length > 0;

  // Occurrences that can be excluded (skipped) from a recurring schedule:
  // virtual events and materialized instances of a recurring series. Standalone
  // one-offs aren't part of a schedule, so they can't be excluded.
  const recurringInstanceIds = selectedRows
    .filter((r) => r.materializationType === SubmissionMaterializationType.Materialized)
    .map((r) => r.submissionId)
    .filter((id): id is string => id != null);
  const excludableCount = selectedVirtualEventIds.length + recurringInstanceIds.length;
  const hasExcludable = excludableCount > 0;
  const hasNonExcludableSelected = selectedRowIds.length > excludableCount;

  const isMultiTenant = useMemo(() => {
    const tenantIds = new Set(
      selectedRows.map((r) => r.tenantId).filter((id): id is string => Boolean(id))
    );
    return tenantIds.size > 1;
  }, [selectedRows]);

  const canCreateRevisions = useMemo(() => {
    const materialized = selectedRows.filter(
      (r) =>
        r.submissionId != null &&
        r.materializationType !== SubmissionMaterializationType.VirtualEvent
    );
    return (
      materialized.length > 0 &&
      materialized.every((r) => r.submissionStatus === SubmissionStatus.FilingComplete)
    );
  }, [selectedRows]);

  const { canChangeEncampStatus, encampStatusSubmissions } = useMemo(() => {
    const materialized = selectedRows.filter(
      (r) =>
        r.submissionId != null &&
        r.materializationType !== SubmissionMaterializationType.VirtualEvent
    );
    if (materialized.length === 0) {
      return { canChangeEncampStatus: false, encampStatusSubmissions: [] };
    }
    const statuses = new Set(materialized.map((r) => r.submissionStatus));
    if (statuses.size !== 1) {
      return { canChangeEncampStatus: false, encampStatusSubmissions: [] };
    }
    const commonStatus = [...statuses][0];
    const hasValidSubStatuses =
      !!commonStatus && (VALID_ENCAMP_STATUSES[commonStatus]?.length ?? 0) > 0;
    return {
      canChangeEncampStatus: hasValidSubStatuses,
      encampStatusSubmissions: materialized.flatMap((r) => {
        if (!r.submissionId) return [];
        return [
          {
            submissionId: r.submissionId,
            submissionStatus: r.submissionStatus ?? "",
            encampStatus: r.submissionEncampStatus,
          },
        ];
      }),
    };
  }, [selectedRows]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // Combined handler for successful bulk actions: refetch data and clear selection
  const handleBulkActionSuccess = useCallback(() => {
    onSuccess();
    onClearSelection();
  }, [onSuccess, onClearSelection]);

  // Note: For inline mutation handlers below, use handleBulkActionSuccess
  // For dialogs, pass handleBulkActionSuccess as the onSuccess prop

  // All mutations include refetchQueries to ensure the grid updates after changes
  const [setAssignees, { loading: assigneesLoading }] = useMutation(
    SET_SUBMISSION_ASSIGNEES_STAFF_MUTATION,
    {
      refetchQueries: ["WasteSubmissionsForStaff", "WasteSubmissionsStaffSummary"],
      awaitRefetchQueries: true,
    }
  );
  const [clearAssignees, { loading: clearAssigneesLoading }] = useMutation(
    CLEAR_SUBMISSION_ASSIGNEES_MUTATION,
    {
      refetchQueries: ["WasteSubmissionsForStaff", "WasteSubmissionsStaffSummary"],
      awaitRefetchQueries: true,
    }
  );
  const [excludeSeriesOccurrences, { loading: excludeLoading }] = useMutation(
    EXCLUDE_SERIES_OCCURRENCES_MUTATION,
    {
      refetchQueries: ["WasteSubmissionsForStaff", "WasteSubmissionsStaffSummary"],
      awaitRefetchQueries: true,
    }
  );

  const handleAssignOwner = async (userId: string) => {
    try {
      const result = await setAssignees({
        variables: {
          submissionIds: selectedSubmissionIds,
          virtualEventIds: selectedVirtualEventIds.length > 0 ? selectedVirtualEventIds : undefined,
          assigneeId: userId,
        },
      });
      if (result.data?.setSubmissionAssignees?.succeeded) {
        const updated = result.data.setSubmissionAssignees.updated ?? 0;
        success(`Assigned Encamp assignee to ${updated} submissions`);
        setAssignOwnerDialogOpen(false);
        handleBulkActionSuccess();
      }
    } catch (e) {
      error("Failed to assign Encamp assignee");
    }
  };

  const handleClearOwner = async () => {
    if (!hasMaterializedSubmissions) {
      return;
    }

    try {
      const result = await clearAssignees({
        variables: {
          submissionIds: selectedSubmissionIds,
        },
      });
      if (result.data?.clearSubmissionAssignees?.succeeded) {
        const updated = result.data.clearSubmissionAssignees.updated ?? 0;
        success(`Cleared Encamp assignee from ${updated} submissions`);
        handleClose();
        handleBulkActionSuccess();
      }
    } catch (e) {
      error("Failed to clear Encamp assignee");
    }
  };

  const handleExcludeOccurrences = async () => {
    if (!hasExcludable) {
      return;
    }

    try {
      await excludeSeriesOccurrences({
        variables: {
          input: {
            submissionIds: recurringInstanceIds,
            virtualEventIds: selectedVirtualEventIds,
          },
        },
      });
      success(
        `Excluded ${excludableCount} ${excludableCount === 1 ? "occurrence" : "occurrences"} from their schedule`
      );
      setExcludeDialogOpen(false);
      handleBulkActionSuccess();
    } catch (e) {
      error("Failed to exclude occurrences", e);
      setExcludeDialogOpen(false);
    }
  };

  const isLoading = assigneesLoading || clearAssigneesLoading || excludeLoading;

  // Build button label
  let buttonLabel = `Bulk Actions (${selectedRowIds.length})`;
  if (hasVirtualEvents && hasMaterializedSubmissions) {
    buttonLabel = `Bulk Actions (${selectedSubmissionIds.length} + ${selectedVirtualEventIds.length} virtual)`;
  } else if (hasVirtualEvents) {
    buttonLabel = `Bulk Actions (${selectedVirtualEventIds.length} virtual)`;
  }

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        endIcon={<ArrowDownIcon />}
        onClick={handleClick}
        disabled={selectedRowIds.length === 0 || isLoading}
      >
        {buttonLabel}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        {hasVirtualEvents && [
          <MenuItem key="virtual-info" disabled sx={{ opacity: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Virtual events will be started when assigned
            </Typography>
          </MenuItem>,
          <Divider key="virtual-divider" />,
        ]}

        {/* Status & Workflow Actions */}
        <MenuItem
          onClick={() => {
            handleClose();
            setChangeStatusDialogOpen(true);
          }}
        >
          <ListItemIcon>
            <SwapHorizIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Change Status...</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleClose();
            setChangeEncampStatusDialogOpen(true);
          }}
          disabled={!canChangeEncampStatus}
        >
          <ListItemIcon>
            <TuneIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Change Encamp Status...</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleClose();
            setChangeDueDateDialogOpen(true);
          }}
        >
          <ListItemIcon>
            <CalendarMonthIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Change Due Date...</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleClose();
            setSendToReviewersDialogOpen(true);
          }}
          disabled={!hasMaterializedSubmissions}
        >
          <ListItemIcon>
            <SendIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Send to Reviewers...</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleClose();
            setCreateRevisionsDialogOpen(true);
          }}
          disabled={!canCreateRevisions}
        >
          <ListItemIcon>
            <ContentCopyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Create Revisions...</ListItemText>
        </MenuItem>

        <Divider />

        {/* Reviewer & Signer Actions */}
        <Tooltip
          title={isMultiTenant ? "Select submissions from a single tenant to add reviewers" : ""}
          placement="right"
        >
          <span>
            <MenuItem
              onClick={() => {
                handleClose();
                setBulkAddReviewersDialogOpen(true);
              }}
              disabled={!hasMaterializedSubmissions || isMultiTenant}
            >
              <ListItemIcon>
                <GroupAddIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Add Reviewers...</ListItemText>
            </MenuItem>
          </span>
        </Tooltip>
        <Tooltip
          title={isMultiTenant ? "Select submissions from a single tenant to set a signer" : ""}
          placement="right"
        >
          <span>
            <MenuItem
              onClick={() => {
                handleClose();
                setBulkSetSignerDialogOpen(true);
              }}
              disabled={!hasMaterializedSubmissions || isMultiTenant}
            >
              <ListItemIcon>
                <DrawIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Set Signer...</ListItemText>
            </MenuItem>
          </span>
        </Tooltip>

        <Divider />

        {/* Staff Assignment Actions */}
        <MenuItem
          onClick={() => {
            handleClose();
            setAssignOwnerDialogOpen(true);
          }}
        >
          <ListItemIcon>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Assign Encamp Assignee...</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleClearOwner} disabled={isLoading || !hasMaterializedSubmissions}>
          <ListItemIcon>
            <PersonOffIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Clear Encamp Assignee</ListItemText>
        </MenuItem>
        <Divider />

        {/* Destructive Actions */}
        <MenuItem
          onClick={() => {
            handleClose();
            setExcludeDialogOpen(true);
          }}
          disabled={isLoading || !hasExcludable}
          sx={{ color: "warning.main" }}
        >
          <ListItemIcon>
            <EventBusyIcon fontSize="small" color="warning" />
          </ListItemIcon>
          <ListItemText
            primary="Exclude from Schedule..."
            secondary={
              hasExcludable && hasNonExcludableSelected
                ? `${excludableCount} of ${selectedRowIds.length} are recurring occurrences`
                : !hasExcludable
                  ? "Only recurring occurrences can be excluded"
                  : undefined
            }
          />
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleClose();
            setDeleteDialogOpen(true);
          }}
          disabled={isLoading || !hasMaterializedSubmissions}
          sx={{ color: "error.main" }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText
            primary="Delete Selected..."
            secondary={
              !hasMaterializedSubmissions
                ? "Only real submissions can be deleted — use Exclude for virtual events"
                : undefined
            }
          />
        </MenuItem>
      </Menu>

      {/* Staff Assignment Dialogs */}
      <AssignDialog
        open={assignOwnerDialogOpen}
        onClose={() => setAssignOwnerDialogOpen(false)}
        onAssign={handleAssignOwner}
        title="Assign Encamp Assignee"
        selectedCount={selectedRowIds.length}
        isLoading={assigneesLoading}
        warningMessage={
          hasVirtualEvents
            ? `${selectedVirtualEventIds.length} virtual event(s) will be started`
            : undefined
        }
      />

      {/* Status & Workflow Dialogs */}
      <ChangeStatusDialog
        open={changeStatusDialogOpen}
        onClose={() => setChangeStatusDialogOpen(false)}
        submissions={selectedRows}
        onSuccess={handleBulkActionSuccess}
      />

      <ChangeEncampStatusDialog
        open={changeEncampStatusDialogOpen}
        onClose={() => setChangeEncampStatusDialogOpen(false)}
        submissions={encampStatusSubmissions}
        onSuccess={handleBulkActionSuccess}
      />

      <ChangeDueDateDialog
        open={changeDueDateDialogOpen}
        onClose={() => setChangeDueDateDialogOpen(false)}
        submissions={selectedRows}
        onSuccess={handleBulkActionSuccess}
      />

      <SendToReviewersDialog
        open={sendToReviewersDialogOpen}
        onClose={() => setSendToReviewersDialogOpen(false)}
        submissions={selectedRows}
        onSuccess={handleBulkActionSuccess}
      />

      <DeleteSubmissionsDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        submissions={selectedRows}
        onSuccess={handleBulkActionSuccess}
      />

      {/* Customer Reviewer Dialogs */}
      <BulkAddReviewersDialog
        open={bulkAddReviewersDialogOpen}
        onClose={() => setBulkAddReviewersDialogOpen(false)}
        submissions={selectedRows.map(toBulkActionSubmission)}
        onSuccess={handleBulkActionSuccess}
        refetchQueries={["WasteSubmissionsForStaff", "WasteSubmissionsStaffSummary"]}
      />

      <BulkSetSignerDialog
        open={bulkSetSignerDialogOpen}
        onClose={() => setBulkSetSignerDialogOpen(false)}
        submissions={selectedRows.map(toBulkActionSubmission)}
        onSuccess={handleBulkActionSuccess}
        refetchQueries={["WasteSubmissionsForStaff", "WasteSubmissionsStaffSummary"]}
      />

      <BulkCreateRevisionSubmissionsDialog
        open={createRevisionsDialogOpen}
        onClose={() => setCreateRevisionsDialogOpen(false)}
        submissions={selectedRows.map(toBulkActionSubmission)}
        onSuccess={handleBulkActionSuccess}
        refetchQueries={["WasteSubmissionsForStaff", "WasteSubmissionsStaffSummary"]}
      />

      {/* Exclude Confirmation Dialog */}
      <ConfirmDialog
        open={excludeDialogOpen}
        loading={excludeLoading}
        onClose={() => setExcludeDialogOpen(false)}
        onConfirm={handleExcludeOccurrences}
        header="Exclude Occurrences from Schedule"
        confirmText="Exclude Occurrences"
      >
        <DialogContentText>
          This will remove {excludableCount} occurrence(s) from the recurring schedule, so they will
          no longer appear in this list or count as due. If an occurrence has a submission, the
          submission and its documents will be deleted.
        </DialogContentText>
        <DialogContentText sx={{ mt: 1 }}>
          Only the selected occurrence dates are excluded — future occurrences will still appear.
          Unlike Delete, excluded occurrences do not return to the schedule as Not Started.
        </DialogContentText>
        {hasNonExcludableSelected && (
          <DialogContentText sx={{ mt: 1, color: "text.secondary" }}>
            Note: {selectedRowIds.length - excludableCount} selected item(s) are not recurring
            occurrences and will be left unchanged.
          </DialogContentText>
        )}
        <DialogContentText sx={{ mt: 2, fontWeight: "bold" }}>
          To reschedule an excluded occurrence, re-add it from the schedule.
        </DialogContentText>
      </ConfirmDialog>
    </>
  );
}
